import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight, FiChevronDown, FiGlobe, FiShare2 } from "react-icons/fi";
import {
  FaSpotify,
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaApple,
  FaSoundcloud,
} from "react-icons/fa";

import MainContainer from "../../../components/utils/MainContainer";
import Seo, { seoExcerpt, toAbsoluteUrl } from "../../../components/utils/Seo";
import {
  HeroDetailSkeleton,
  ProductGridSkeleton,
} from "../../../components/utils/Skeleton";
import ProductCard from "../../../components/portal/ProductCard";
import NewsCard from "../../../components/portal/NewsCard";
import { toast } from "../../../components/modern-ui/sonner";
import { slugify } from "../../../utils/utils";
import useReveal from "../../../hooks/useReveal";

import productsService from "../../../api/services/products";
import newsService from "../../../api/services/news";

import { selectPublicBandDetails } from "../../../store/slices/bands/slice";
import {
  getBandByName,
  getBandPublicDetails,
} from "../../../store/slices/bands/asyncThunks";
import { trackBandView } from "../../../store/slices/viewTracking/asyncThunks";

import {
  Hero,
  HeroInner,
  HeroName,
  HeroMetaRow,
  HeroActions,
  SocialButton,
  Page,
  Section,
  SectionLabel,
  SectionH2,
  Prose,
  BioClamp,
  BioToggle,
  MembersRow,
  MemberChip,
  ProductsGrid,
  NewsGrid,
  RevealCell,
  SectionFooter,
  GhostButton,
  EmptyNote,
  StateContainer,
} from "./BandDetailsPageNew.styles";

const SOCIAL_ICONS = {
  spotify: FaSpotify,
  youtube: FaYoutube,
  instagram: FaInstagram,
  facebook: FaFacebookF,
  twitter: FaTwitter,
  apple: FaApple,
  soundcloud: FaSoundcloud,
  website: FiGlobe,
};

// Backend za listu proizvoda vraća grupisan format po kategorijama
const flattenProducts = (data) => {
  if (!Array.isArray(data)) return [];
  if (data[0]?.categoryName && data[0]?.products) {
    return data.flatMap((category) =>
      Array.isArray(category.products) ? category.products : [],
    );
  }
  return data;
};

/* Grid ćelija sa sopstvenim scroll-reveal observerom; --i je kolona u redu */
const RevealGridCell = ({ columnIndex, children }) => {
  const [ref, visible] = useReveal({ rootMargin: "0px 0px -40px 0px" });
  return (
    <RevealCell ref={ref} $visible={visible} style={{ "--i": columnIndex }}>
      {children}
    </RevealCell>
  );
};

export default function BandDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: bandData,
    loading,
    error,
  } = useSelector(selectPublicBandDetails);

  const { bandId } = useParams();
  const location = useLocation();
  const bandName = location.state?.bandName;

  const [products, setProducts] = useState(null); // null = učitava se
  const [bandNews, setBandNews] = useState([]);

  // Biografija skraćena na 3 reda; dugme se prikazuje samo ako je stvarno duža
  const bioClampRef = useRef(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  const [bioFullHeight, setBioFullHeight] = useState(0);

  // Scroll-reveal po sekcijama
  const [bioRef, bioVisible] = useReveal();
  const [merchRef, merchVisible] = useReveal();
  const [newsRef, newsVisible] = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (bandId) {
      dispatch(getBandPublicDetails(bandId));
      dispatch(trackBandView(bandId));
    } else if (bandName) {
      dispatch(getBandByName(bandName));
    }
  }, [dispatch, bandId, bandName]);

  // Merch benda, svi proizvodi (backend paginira po 12)
  useEffect(() => {
    if (!bandData?.id) return undefined;
    let active = true;

    const loadProducts = async () => {
      try {
        const first = await productsService.getProducts({
          page: 1,
          bandId: bandData.id,
        });
        const firstItems = flattenProducts(first?.data);
        const perPage = Number(first?.perPage) || 12;
        const total = Number(first?.total) || firstItems.length;
        const totalPages = Math.min(Math.ceil(total / perPage) || 1, 10);

        let rest = [];
        if (totalPages > 1) {
          const responses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              productsService.getProducts({
                page: i + 2,
                bandId: bandData.id,
              }),
            ),
          );
          rest = responses.flatMap((response) =>
            flattenProducts(response?.data),
          );
        }

        if (!active) return;
        const seen = new Set();
        setProducts(
          [...firstItems, ...rest].filter((product) => {
            if (!product?.id || seen.has(product.id)) return false;
            seen.add(product.id);
            return true;
          }),
        );
      } catch {
        if (active) setProducts([]);
      }
    };

    loadProducts();
    return () => {
      active = false;
    };
  }, [bandData?.id]);

  // Novosti benda, iz najnovijih novosti (API nema filter po bendu)
  useEffect(() => {
    if (!bandData?.id) return undefined;
    let active = true;

    const loadNews = async () => {
      try {
        const [page1, page2] = await Promise.all([
          newsService.getNews({ page: 1 }),
          newsService.getNews({ page: 2 }).catch(() => null),
        ]);
        if (!active) return;
        const all = [...(page1?.data || []), ...(page2?.data || [])];
        setBandNews(
          all
            .filter((item) => String(item.bandId) === String(bandData.id))
            .slice(0, 3),
        );
      } catch {
        if (active) setBandNews([]);
      }
    };

    loadNews();
    return () => {
      active = false;
    };
  }, [bandData?.id]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${bandData.name}`,
          text: `Pogledajte informacije o bendu ${bandData.name}`,
          url: window.location.href,
        });
      } catch (error) {
        // Otkazan share dijalog nije greška
        if (error.name !== "AbortError") {
          navigator.clipboard.writeText(window.location.href);
          toast.success("Link je kopiran u clipboard");
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link je kopiran u clipboard");
    }
  };

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const socials = useMemo(() => {
    const links = bandData?.socialLinks || {};
    return Object.entries(SOCIAL_ICONS)
      .filter(([key]) => links[key])
      .map(([key, Icon]) => ({ key, Icon, url: links[key] }));
  }, [bandData?.socialLinks]);

  const members = useMemo(() => {
    if (!Array.isArray(bandData?.members)) return [];
    return bandData.members
      .map((member) =>
        typeof member === "string"
          ? { name: member, role: "" }
          : { name: member?.name || "", role: member?.role || "" },
      )
      .filter((member) => member.name);
  }, [bandData?.members]);

  const bioParagraphs = useMemo(() => {
    const bio = bandData?.biography || bandData?.description || "";
    return bio
      .split(/\n{2,}|\r\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }, [bandData?.biography, bandData?.description]);

  // Merenje: da li bio prelazi 3 reda; radi i posle otvaranja (poredi punu
  //  visinu sadržaja sa visinom skraćenog stanja, ne sa trenutnom visinom)
  useEffect(() => {
    setBioExpanded(false);
  }, [bandData?.id]);

  useEffect(() => {
    const el = bioClampRef.current;
    if (!el) return undefined;

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 0;
      setBioFullHeight(el.scrollHeight);
      setBioOverflows(el.scrollHeight > lineHeight * 3 + 2);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [bioParagraphs]);

  const coverImage =
    bandData?.bannerImage ||
    bandData?.gallery?.find((img) => img.isBanner)?.url ||
    bandData?.gallery?.[0]?.url ||
    bandData?.mainImage ||
    null;

  if (loading) {
    return (
      <MainContainer>
        <HeroDetailSkeleton />
      </MainContainer>
    );
  }

  if (error || !bandData) {
    return (
      <MainContainer>
        <StateContainer>
          {error
            ? `Greška prilikom učitavanja benda: ${error}`
            : "Bend nije pronađen."}
        </StateContainer>
      </MainContainer>
    );
  }

  const seoPath = `/bend/${bandData.slug || bandId || bandData.id}`;

  return (
    <MainContainer>
      {/* SEO, meta iz podataka benda + MusicGroup schema za Google */}
      <Seo
        title={bandData.name}
        description={seoExcerpt(bandData.biography || bandData.description)}
        canonical={seoPath}
        image={coverImage}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MusicGroup",
          name: bandData.name,
          ...(coverImage ? { image: toAbsoluteUrl(coverImage) } : {}),
          url: toAbsoluteUrl(seoPath),
        }}
      />
      {/* Hero */}
      <Hero>
        {coverImage && (
          <img className="cover" src={coverImage} alt={bandData.name} />
        )}
        <HeroInner>
          <HeroName>{bandData.name}</HeroName>
          <HeroMetaRow>
            {bandData.genre && <span>{bandData.genre}</span>}
            {bandData.genre && bandData.city && <span className="dot">·</span>}
            {bandData.city && <span>{bandData.city}</span>}
            {bandData.founded && (
              <>
                <span className="dot">·</span>
                <span>osnovan {bandData.founded}.</span>
              </>
            )}
            {members.length > 0 && (
              <>
                <span className="dot">·</span>
                <span>
                  {members.length} {members.length === 1 ? "član" : "članova"}
                </span>
              </>
            )}
          </HeroMetaRow>
          <HeroActions>
            {socials.map(({ key, Icon, url }) => (
              <SocialButton
                key={key}
                type="button"
                title={key}
                aria-label={key}
                onClick={() =>
                  window.open(url, "_blank", "noopener,noreferrer")
                }
              >
                <Icon size={15} />
              </SocialButton>
            ))}
            <SocialButton
              type="button"
              title="Podeli"
              aria-label="Podeli"
              onClick={handleShare}
            >
              <FiShare2 size={15} />
            </SocialButton>
          </HeroActions>
        </HeroInner>
      </Hero>

      <Page>
        {/* Bio */}
        {(bioParagraphs.length > 0 || members.length > 0) && (
          <Section ref={bioRef}>
            <SectionLabel $visible={bioVisible}>Bio</SectionLabel>
            <Prose $visible={bioVisible}>
              <BioClamp
                ref={bioClampRef}
                $collapsed={!bioExpanded}
                $fade={!bioExpanded && bioOverflows}
                style={
                  bioExpanded && bioFullHeight
                    ? { maxHeight: `${bioFullHeight}px` }
                    : undefined
                }
              >
                {bioParagraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </BioClamp>
              {bioOverflows && (
                <BioToggle
                  type="button"
                  aria-expanded={bioExpanded}
                  $expanded={bioExpanded}
                  onClick={() => setBioExpanded((prev) => !prev)}
                >
                  {bioExpanded ? "Prikaži manje" : "Pročitaj još"}
                  <FiChevronDown size={15} />
                </BioToggle>
              )}
            </Prose>
            {members.length > 0 && (
              <MembersRow $visible={bioVisible}>
                {members.map((member, index) => (
                  <MemberChip key={index}>
                    {member.name}
                    {member.role && <small>{member.role}</small>}
                  </MemberChip>
                ))}
              </MembersRow>
            )}
          </Section>
        )}

        {/* Merch, jezgro stranice */}
        <Section ref={merchRef}>
          <SectionLabel $visible={merchVisible}>Merch</SectionLabel>
          <SectionH2 $visible={merchVisible}>
            Merch
            {Array.isArray(products) && (
              <small>
                {products.length}{" "}
                {products.length === 1 ? "proizvod" : "proizvoda"}
              </small>
            )}
          </SectionH2>

          {products === null ? (
            <ProductGridSkeleton count={4} />
          ) : products.length === 0 ? (
            <EmptyNote>Ovaj bend trenutno nema proizvoda u ponudi.</EmptyNote>
          ) : (
            <>
              <ProductsGrid>
                {products.map((product, index) => (
                  <RevealGridCell key={product.id} columnIndex={index % 4}>
                    <ProductCard product={product} />
                  </RevealGridCell>
                ))}
              </ProductsGrid>
              <SectionFooter>
                <GhostButton
                  type="button"
                  onClick={() =>
                    goTo(`/prodavnica?bend=${slugify(bandData.name)}`)
                  }
                >
                  Otvori u prodavnici <FiArrowRight size={16} />
                </GhostButton>
              </SectionFooter>
            </>
          )}
        </Section>

        {/* Novosti benda */}
        {bandNews.length > 0 && (
          <Section ref={newsRef}>
            <SectionLabel $visible={newsVisible}>Novosti</SectionLabel>
            <SectionH2 $visible={newsVisible}>Novosti benda</SectionH2>
            <NewsGrid>
              {bandNews.map((news, index) => (
                <RevealGridCell key={news.id} columnIndex={index % 3}>
                  <NewsCard news={news} />
                </RevealGridCell>
              ))}
            </NewsGrid>
          </Section>
        )}
      </Page>
    </MainContainer>
  );
}
